import { useState } from "react";
import { useIntl } from "react-intl";
import axios from "axios";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { toast } from "react-toastify";
import Active from "../../../../assets/images/accountStatus/Active.svg";
import Pending from "../../../../assets/images/accountStatus/Pending.svg";

import useStyles from "./bankAccountsStyles";
import { SrButton, Typography } from "../../../common";
import translate from "../../../../i18n/translate";
import { BANK_ACCOUNTS_ID } from "../../../../constants/api";

const StatusBadge = ({ status, origin }) => {
  const classes = useStyles();
  const intl = useIntl();

  const EXTERNAL_BANK_ACCOUNT = 2;

  const statuses = {
    active: {
      msg: intl.formatMessage({ id: "common.active" }),
      styles: classes.statusActive,
    },
    pending: {
      msg: intl.formatMessage({ id: "common.inReview" }),
      styles: classes.statusInReview,
    },
    rejected: {
      msg: intl.formatMessage({ id: "common.rejected" }),
      styles: classes.statusRejected,
    },
  };

  const isExternalBankAccount = (bankOrigin) =>
    bankOrigin === EXTERNAL_BANK_ACCOUNT;

  const getLabel = (bankStatus, bankOrigin) =>
    !isExternalBankAccount(bankOrigin)
      ? statuses[bankStatus].msg
      : `${statuses[bankStatus].msg} - ${intl.formatMessage({
          id: "common.thirdParty",
        })}`;

  return (
    <>
      <Box ml={0.5}>
        {status === "active" ? (
          <img
            src={Active}
            alt={intl.formatMessage({ id: "common.thirdParty" })}
          />
        ) : (
          <img
            src={Pending}
            alt={intl.formatMessage({ id: "common.thirdParty" })}
          />
        )}
      </Box>
      <Typography variant="h5Smaller" className={classes.statusName}>
        {getLabel(status, origin)}
      </Typography>
    </>
  );
};

const BankAccountsCard = ({
  id,
  accountNo,
  accountHolder,
  status,
  bankName,
  isDefault,
  handleSetAsDefault,
  origin,
  isDefaultable,
}) => {
  const classes = useStyles();
  const [settingDefault, setSettingDefault] = useState(false);

  // This logic was extracted from the legacy My
  const accountType = id === 0 ? 2 : 1;
  const accountId = accountType === 1 ? id : accountNo;

  const pseudoHideNo = (number) =>
    `XXXX XXXX XXXX ${number.replace(/ /g, "").slice(-4)}`;

  const setAsDefault = async () => {
    setSettingDefault(true);

    try {
      const params = { default: true, type: accountType };

      await axios.put(BANK_ACCOUNTS_ID.replace("{id}", accountId), params);

      handleSetAsDefault(id);
      setSettingDefault(false);
    } catch (error) {
      setSettingDefault(false);
      toast.error(error?.response?.data?.error?.message ?? error.message);
    }
  };
  return (
    <Box
      className={classes.accountsCard}
      alignItems="center"
      bgcolor="white"
      borderRadius="borderRadius"
      border="1px solid"
      borderColor="white"
      mb={4}
      p={2}
    >
      <Box className={classes.accountHolder}>
        <Typography variant="h5">{accountHolder}</Typography>
        <Typography>{pseudoHideNo(accountNo)}</Typography>
      </Box>

      <Box className={classes.accountInfo}>
        <Box className={classes.accountBank}>
          <Typography variant="subtitle1">{bankName}</Typography>
        </Box>
        <Box className={classes.accountStatus}>
          <Box>
            <StatusBadge status={status} origin={origin} />
            {isDefault ? (
              <SrButton
                className={classes.buttonAction}
                variant="contained"
                size="small"
                onClick={setAsDefault}
                disabled
              >
                {translate("bankAccounts.default")}
              </SrButton>
            ) : (
              isDefaultable && (
                <SrButton
                  className={classes.buttonAction}
                  variant="contained"
                  size="small"
                  disabled={settingDefault}
                  onClick={setAsDefault}
                >
                  {translate("bankAccounts.setAsDefault")}
                </SrButton>
              )
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

BankAccountsCard.propTypes = {
  id: PropTypes.number.isRequired,
  accountNo: PropTypes.string.isRequired,
  accountHolder: PropTypes.string.isRequired,
  status: PropTypes.oneOf(["active", "pending", "rejected"]).isRequired,
  bankName: PropTypes.string.isRequired,
  isDefault: PropTypes.bool.isRequired,
  handleSetAsDefault: PropTypes.func.isRequired,
  origin: PropTypes.number,
  isDefaultable: PropTypes.bool.isRequired,
};

BankAccountsCard.defaultProps = {
  origin: null,
};

export default BankAccountsCard;
